import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import {
  GET_MY_AMCR_ACCOUNT_QUERY,
  GetMyFindQueryName,
  MARK_AMCR_FIND_SUBMITTED_MUTATION,
  reconcileFindsInCache,
} from '../../gql';
import { Button } from '../design-system';
import { AmcrSignInForm } from './AmcrSignInForm';
import { AmcrErrorDetail, getAmcrProblemMessage } from './messages';

interface AmcrPasSubmitDialogProps {
  findId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const SIGN_IN_REQUIRED_PATH = 'amcr.unauthorized';

const submitMessages = defineMessages({
  title: {
    id: 'sitePas.submit.title',
    defaultMessage: 'Submit',
  },
  confirm: {
    id: 'sitePas.submit.confirm',
    defaultMessage:
      'Are you sure you want to submit this find for approval to the AMCR PAS authority?',
  },
  failed: {
    id: 'sitePas.submit.failed',
    defaultMessage: 'The find could not be submitted.',
  },
});

const contentStyles = { minWidth: { sm: 420 } };
const loadingStyles = { py: 4, alignItems: 'center' };
const errorListStyles = { m: 0, pl: 2 };
const hiddenStyles = { display: 'none' };

const AmcrPasSubmitDialog = ({
  findId,
  onClose,
  onSubmitted,
}: AmcrPasSubmitDialogProps) => {
  const intl = useIntl();
  const [isSignInRequired, setIsSignInRequired] = useState(false);
  const [errors, setErrors] = useState<AmcrErrorDetail[]>([]);

  const accountQuery = useQuery(GET_MY_AMCR_ACCOUNT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const account = accountQuery.data?.getMyAmcrAccount;
  const hasValidAccount = Boolean(account && !account.expired);
  const isSignedIn = hasValidAccount && !isSignInRequired;

  const [markSubmitted, { loading: submitting }] = useMutation(
    MARK_AMCR_FIND_SUBMITTED_MUTATION,
    {
      refetchQueries: [GetMyFindQueryName],
      update: (cache, { data }) => {
        const updatedFind = data?.markAmcrFindSubmitted?.find;

        if (updatedFind) {
          reconcileFindsInCache(cache, [updatedFind.id]);
        }
      },
    }
  );

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    setErrors([]);

    try {
      const { data } = await markSubmitted({ variables: { findId } });
      const result = data?.markAmcrFindSubmitted;

      if (result?.find) {
        onSubmitted();
        onClose();
        return;
      }

      const resultErrors: AmcrErrorDetail[] = result?.errors || [];

      if (resultErrors.some(({ path }) => path === SIGN_IN_REQUIRED_PATH)) {
        setIsSignInRequired(true);
        return;
      }

      setErrors(resultErrors);
    } catch {
      setErrors([
        {
          path: 'amcr',
          error: intl.formatMessage(submitMessages.failed),
        },
      ]);
    }
  };

  const handleSignedIn = () => {
    setIsSignInRequired(false);
  };

  const renderConfirm = () => (
    <Box sx={isSignedIn ? undefined : hiddenStyles}>
      <DialogContent sx={contentStyles}>
        <Stack gap={2}>
          <DialogContentText>
            <FormattedMessage {...submitMessages.confirm} />
          </DialogContentText>
          {errors.length > 0 && (
            <Alert severity="error" variant="outlined">
              <Stack component="ul" sx={errorListStyles}>
                {errors.map((detail) => (
                  <li key={`${detail.path}:${detail.error}`}>
                    {getAmcrProblemMessage(intl, detail)}
                  </li>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button tone="neutral" disabled={submitting} onClick={handleClose}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button disabled={submitting} onClick={handleSubmit}>
          <FormattedMessage id="sitePas.action.submit" defaultMessage="Submit" />
        </Button>
      </DialogActions>
    </Box>
  );

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage {...submitMessages.title} />
      </DialogTitle>
      {accountQuery.loading && (
        <DialogContent sx={contentStyles}>
          <Stack sx={loadingStyles}>
            <CircularProgress size={32} />
          </Stack>
        </DialogContent>
      )}
      {!accountQuery.loading && !isSignedIn && (
        <AmcrSignInForm
          isSessionExpired={Boolean(account) || isSignInRequired}
          onCancel={handleClose}
          onSignedIn={handleSignedIn}
        />
      )}
      {!accountQuery.loading && hasValidAccount && renderConfirm()}
    </Dialog>
  );
};

export { AmcrPasSubmitDialog };
